import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { memo, PropsWithChildren, useCallback, useMemo, useState } from 'react'

import { IPartner } from 'clickNgo/entities/partner/models'
import { formatErrorMessages } from 'clickNgo/helpers/api'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { TForm, useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS } from '../../translations'

import { PartnerInactivePanel } from './components/partnerInactivatePanel/PartnerInactivatePanel'
import { useInactivatePartner } from './hooks/useInactivatePartner'
import { useMandatoryAttachmentsForm } from './hooks/useMandatoryAttachmentsForm'

import styles from './PartnerDataFormContainer.module.scss'

interface IPartnerDataFormContainerProps {
  formKey: string
  isAttachmentRequired?: boolean
  onApply?: (formData: TForm<IPartner>) => Promise<boolean>
  onCancel?: (formData: TForm<IPartner>) => void
  isDisabled?: boolean
  isSaveAsDraft?: boolean
}

export const PartnerDataFormContainer = memo<PropsWithChildren<IPartnerDataFormContainerProps>>(
  ({ onApply, onCancel, formKey, children, isAttachmentRequired, isDisabled, isSaveAsDraft }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [isInactivatePartnerDialogVisible, setIsInactivatePartnerDialogVisible] = useState(false)
    // the type is not really IPartner, for different formKey it's a part of IPartner
    const [formData] = useGetFormData<IPartner>(formKey)
    const {
      isInactive,
      inactivatePartner,
      isPartnerInactivationPossible,
      partnerInactivationUnpossibleMessage,
      partnerInactivationErrorMessages,
    } = useInactivatePartner()

    const formattedInactivationErrorMessages = useMemo(
      () => formatErrorMessages(partnerInactivationErrorMessages),
      [partnerInactivationErrorMessages]
    )

    const applyChanges = useCallback(() => {
      if (onApply) {
        setIsLoading(true)
        onApply(formData).finally(() => {
          setIsLoading(false)
        })
      }
    }, [onApply, formData])

    const discardChanges = useCallback(() => {
      if (onCancel) {
        onCancel(formData)
      }
    }, [onCancel, formData])

    const {
      handleOnMandatoryAttachmentsConfirm,
      handleOnMandatoryAttachmentsReject,
      hasMandatoryAttachments,
      isMandatoryAttachmentsDialogVisible,
      setIsMandatoryAttachmentsDialogVisible,
      mandatoryAttachmentsContent,
    } = useMandatoryAttachmentsForm({
      onConfirm: applyChanges,
    })

    /** ---------------------- Handlers ------------------------ */

    const handleOnPartnerInactivateConfirm = useCallback(() => {
      if (!isDisabled) {
        setIsInactivatePartnerDialogVisible(false)
        setIsLoading(true)
        inactivatePartner().finally(() => {
          setIsLoading(false)
        })
      }
    }, [inactivatePartner, isDisabled])

    const handleOnPartnerInactivateReject = useCallback(() => {
      setIsInactivatePartnerDialogVisible(false)
    }, [])

    const handleOnInactiveClick = useCallback(() => {
      setIsInactivatePartnerDialogVisible(true)
    }, [])

    const handleOnApplyClick = useCallback(() => {
      if (isAttachmentRequired) {
        setIsMandatoryAttachmentsDialogVisible(true)
      } else {
        applyChanges()
      }
    }, [applyChanges, isAttachmentRequired, setIsMandatoryAttachmentsDialogVisible])

    const handleOnCancelClick = useCallback(() => {
      discardChanges()
    }, [discardChanges])

    return (
      <div className={styles.container}>
        <LoaderOverlay show={isLoading} />
        <header className={styles.header}>
          <div className={styles.actions}>
            <RenderWhen condition={!isInactive}>
              <Tooltip
                disabled={isPartnerInactivationPossible}
                tipContent={() => <>{partnerInactivationUnpossibleMessage}</>}
              >
                <Button
                  disabled={!isPartnerInactivationPossible || isDisabled}
                  onClick={handleOnInactiveClick}
                >
                  <Translation
                    text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.container.button.inactivate}
                  />
                </Button>
              </Tooltip>
            </RenderWhen>
            <Button disabled={formData.modifiedFields.length === 0} onClick={handleOnApplyClick}>
              <Translation
                text={
                  isSaveAsDraft
                    ? ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.container.button.saveAsDraft
                    : ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.container.button.apply
                }
              />
            </Button>
            <Button
              color={'danger'}
              onClick={handleOnCancelClick}
              disabled={formData.modifiedFields.length === 0}
            >
              <Translation text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.container.button.cancel} />
            </Button>
          </div>
        </header>
        <RenderWhen condition={isInactive}>
          <PartnerInactivePanel />
        </RenderWhen>
        <RenderWhen condition={Boolean(partnerInactivationErrorMessages.length)}>
          <InfoBlock
            text={formattedInactivationErrorMessages}
            type={InfoBlockType.Danger}
            hasCloseButton
          />
        </RenderWhen>
        <div className={styles.content}>{children}</div>
        <ConfirmDialog
          title={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.mandatoryAttachments.title}
          confirmBtnText={
            ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.mandatoryAttachments.confirmBtn
          }
          rejectBtnText={
            ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.mandatoryAttachments.rejectBtn
          }
          isVisible={isMandatoryAttachmentsDialogVisible}
          content={mandatoryAttachmentsContent}
          onConfirm={handleOnMandatoryAttachmentsConfirm}
          onReject={handleOnMandatoryAttachmentsReject}
          isApplyDisabled={!hasMandatoryAttachments}
        />
        <ConfirmDialog
          title={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.inactivatePartner.title}
          confirmBtnText={
            ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.inactivatePartner.confirmBtn
          }
          rejectBtnText={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.inactivatePartner.rejectBtn}
          isVisible={isInactivatePartnerDialogVisible}
          content={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.inactivatePartner.content}
          onConfirm={handleOnPartnerInactivateConfirm}
          onReject={handleOnPartnerInactivateReject}
        />
      </div>
    )
  }
)
